/**
 * Monitor Page — Unified sprint build & telemetry view
 *
 * Four tabs: Overview | Plan | Events | Sprites
 * Replaces the old EvidencePage (/ev) and SprintTelemetryPage (/tl).
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Activity, CheckCircle, XCircle, ChevronDown, CheckCircle2, MessageCircle } from 'lucide-react';
import { useProject } from '../contexts/ProjectContext';
import { useSprint } from '../contexts/SprintContext';
import type { Sprint } from '../api/sprint';
import { useSprintTelemetry } from '../hooks/useSprintTelemetry';
import { useExecution } from '../hooks/useSprintData';
import { useUnifiedTimeline, useChildWorkflowTree, useSprintErrorSummary } from '../api/telemetry';
import { DevPageHeader } from '../components/layout/DevPageHeader';
import SprintHealthBanner from '../components/telemetry/SprintHealthBanner';
import { MonitorOverviewTab } from '../components/monitor/MonitorOverviewTab';
import { MonitorPlanTab } from '../components/monitor/MonitorPlanTab';
import { MonitorEventsTab } from '../components/monitor/MonitorEventsTab';
import { MonitorSpritesTab } from '../components/monitor/MonitorSpritesTab';
import { PageErrorBoundary } from '../components/ErrorBoundary';
import { topicsApi } from '../api/topics';

type Tab = 'overview' | 'plan' | 'events' | 'sprites';

const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'plan', label: 'Plan' },
  { key: 'events', label: 'Events' },
  { key: 'sprites', label: 'Sprites' },
];

const STATUS_DOT: Record<string, string> = {
  building: 'bg-coral-500',
  planning: 'bg-neutral-400',
  completed: 'bg-emerald-500',
  failed: 'bg-red-500',
};

function SprintSwitcher({ current, allSprints, onSelect }: {
  current: Sprint | null;
  allSprints: Sprint[];
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!current || allSprints.length <= 1) {
    return <span className="text-[11px] text-neutral-400">{current?.name || 'No sprint'}</span>;
  }

  const groups = ['building', 'failed', 'planning', 'completed'] as const;
  const grouped = groups.map(status => ({
    status,
    sprints: allSprints.filter(s => s.status === status),
  })).filter(g => g.sprints.length > 0);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-[11px] text-neutral-500 hover:text-neutral-700 transition-colors"
      >
        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[current.status] || 'bg-neutral-400'} ${current.status === 'building' ? 'animate-pulse' : ''}`} />
        <span className="font-medium">{current.name}</span>
        <ChevronDown size={10} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1.5 z-50 min-w-[260px] bg-white rounded-lg shadow-lg border border-neutral-200 py-1 max-h-[400px] overflow-y-auto">
            {grouped.map(group => (
              <div key={group.status}>
                <div className="px-3 py-1 text-[9px] font-bold uppercase tracking-widest text-neutral-400">
                  {group.status}
                </div>
                {group.sprints.map(s => (
                  <button
                    key={s.id}
                    onClick={() => { onSelect(s.id); setOpen(false); }}
                    className={`w-full text-left px-3 py-1.5 text-[12px] transition-colors flex items-center gap-2 ${
                      s.id === current.id
                        ? 'bg-coral-50 text-coral-700 font-medium'
                        : 'text-neutral-600 hover:bg-neutral-50'
                    }`}
                  >
                    {s.status === 'completed' && <CheckCircle2 size={11} className="text-emerald-500 shrink-0" />}
                    {s.status === 'failed' && <XCircle size={11} className="text-red-500 shrink-0" />}
                    {s.status === 'building' && (
                      <span className="relative flex h-1.5 w-1.5 shrink-0">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-coral-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-coral-500" />
                      </span>
                    )}
                    {s.status === 'planning' && <span className="w-1.5 h-1.5 rounded-full bg-neutral-400 shrink-0" />}
                    <span className="truncate">{s.name}</span>
                    <span className="ml-auto text-[10px] text-neutral-400 font-mono shrink-0">{s.epicIds.length}ep</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

interface MonitorPageProps {
  initialTab?: Tab;
}

export default function MonitorPage({ initialTab }: MonitorPageProps = {}) {
  const { project } = useProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as Tab | null;
  const sprintParam = searchParams.get('sprint');
  const topicParam = searchParams.get('topic');
  const [activeTab, setActiveTab] = useState<Tab>(
    tabParam && TABS.some(t => t.key === tabParam) ? tabParam : (initialTab || 'overview')
  );

  // Optional topic context — shows which topic initiated this build
  const { data: linkedTopic } = useQuery({
    queryKey: ['topic', topicParam],
    queryFn: () => topicsApi.get(topicParam!),
    enabled: !!topicParam,
    staleTime: 60_000,
  });

  const { selectedSprint: contextSprint, allSprints, selectSprint } = useSprint();

  useEffect(() => {
    if (tabParam && TABS.some(t => t.key === tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const handleTabChange = useCallback((tab: Tab) => {
    setActiveTab(tab);
    const params: Record<string, string> = {};
    if (tab !== 'overview') params.tab = tab;
    if (sprintParam) params.sprint = sprintParam;
    if (topicParam) params.topic = topicParam;
    setSearchParams(params, { replace: true });
  }, [setSearchParams, sprintParam, topicParam]);

  const {
    sprint: resolvedSprint,
    epics,
    issueExecutions,
    sprites,
    stream,
    sprintEvents,
    burndownData,
    telemetryEvents,
    sprintPhases,
    sprintPlan,
    qualityGates,
    tokenUsage,
    isLoading,
  } = useSprintTelemetry(project?.id, contextSprint?.id ?? sprintParam ?? null);

  const sprint = contextSprint || resolvedSprint;
  const { data: execution } = useExecution(sprint?.batchId);
  const isLive = stream.isConnected;

  const batchWorkflowId = sprint?.batchId;
  const { data: errorSummary } = useSprintErrorSummary(batchWorkflowId);
  const { data: timelineData } = useUnifiedTimeline(
    activeTab === 'plan' ? batchWorkflowId : undefined
  );
  const { data: childTree } = useChildWorkflowTree(
    activeTab === 'plan' ? batchWorkflowId : undefined
  );

  // Status badge
  const statusBadge = sprint ? (() => {
    const styles: Record<string, string> = {
      building: 'bg-coral-100 text-coral-700',
      planning: 'bg-neutral-100 text-neutral-600',
      completed: 'bg-emerald-50 text-emerald-600',
      failed: 'bg-red-100 text-red-700',
    };
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${styles[sprint.status] || styles.planning}`}>
        {sprint.status === 'building' && <span className="w-1.5 h-1.5 rounded-full bg-coral-500 animate-pulse" />}
        {sprint.status}
      </span>
    );
  })() : undefined;

  const liveIndicator = isLive ? (
    <div className="flex items-center gap-1.5">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
      </span>
      <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-600">Live</span>
    </div>
  ) : undefined;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <DevPageHeader icon={Activity} title="Monitor" subtitle="Loading..." />
        <div className="flex items-center justify-center py-24">
          <div className="w-6 h-6 border-2 border-neutral-300 border-t-coral-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const progress = execution?.progress;
  const executionState = execution?.executionState;

  return (
    <div className="min-h-screen bg-neutral-50">
      <DevPageHeader
        icon={Activity}
        title="Monitor"
        subtitle={<SprintSwitcher current={sprint} allSprints={allSprints} onSelect={selectSprint} />}
        statusBadge={statusBadge}
        actions={liveIndicator}
      />

      {/* Execution progress bar */}
      {sprint?.status === 'building' && (progress || executionState) && (
        <div className="bg-white border-b border-neutral-200 px-6 py-2">
          <div className="max-w-[1400px] mx-auto flex items-center gap-3">
            {progress ? (
              <>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-coral-500 shrink-0">
                  {progress.step}
                </span>
                <div className="flex-1 max-w-[320px] h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500 ease-out bg-coral-500"
                    style={{ width: `${Math.min(100, Math.max(0, progress.progress))}%` }}
                  />
                </div>
                <span className="text-[11px] font-mono text-neutral-500 shrink-0">
                  {Math.round(progress.progress)}%
                </span>
                <span className="text-[11px] text-neutral-400 truncate">
                  {progress.message}
                </span>
              </>
            ) : executionState ? (
              <span className="text-[11px] text-neutral-500 font-mono">
                {executionState.epicsCompleted}/{executionState.epicsTotal} epics
                {' \u00b7 '}
                {executionState.storiesCompleted}/{executionState.storiesTotal} stories
                {executionState.errors.length > 0 && (
                  <span className="text-red-500 ml-2">
                    {executionState.errors.length} error{executionState.errors.length !== 1 ? 's' : ''}
                  </span>
                )}
              </span>
            ) : null}
          </div>
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-6 py-5 space-y-5">
        <SprintHealthBanner workflowId={batchWorkflowId} totalStories={issueExecutions.length} sprintStatus={sprint?.status} />

        {/* Topic context banner — shows when accessed via ?topic= */}
        {linkedTopic && (
          <div className="rounded-lg border border-neutral-200 bg-white px-4 py-3 flex items-center gap-3">
            <MessageCircle size={16} className="text-coral-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">Topic Context</p>
              <p className="text-sm font-medium text-neutral-800 truncate">{(linkedTopic as { topic?: { title: string } }).topic?.title ?? 'Unknown topic'}</p>
            </div>
            <a
              href={`?topic=${topicParam}`}
              className="text-[11px] font-medium text-coral-500 hover:text-coral-600 transition-colors"
            >
              Open topic
            </a>
          </div>
        )}

        {/* Sprint outcome banners */}
        {sprint?.status === 'failed' && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-5 py-4 flex items-start gap-3">
            <XCircle size={20} className="text-red-500 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-red-800">Sprint Failed</p>
              <p className="text-xs text-red-600 mt-1">
                {errorSummary?.topError?.errorText || 'The sprint workflow was terminated due to verification failures or infrastructure errors.'}
              </p>
              <div className="flex items-center gap-3 mt-2.5">
                <button onClick={() => handleTabChange('events')} className="text-[11px] font-medium text-red-600 hover:text-red-700 transition-colors underline underline-offset-2">
                  View errors in Events tab
                </button>
              </div>
            </div>
          </div>
        )}
        {sprint?.status === 'completed' && (
          <div className="rounded-lg border border-accent-200 bg-accent-50 px-5 py-3 flex items-center gap-3">
            <CheckCircle size={18} className="text-accent-600 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-accent-800">Sprint completed successfully</p>
              {sprint.completedAt && (
                <p className="text-xs text-accent-600 mt-0.5">Completed {new Date(sprint.completedAt).toLocaleString()}</p>
              )}
            </div>
          </div>
        )}

        {/* Tab bar */}
        <div className="flex items-center gap-0.5 border-b border-neutral-200 bg-neutral-50/50 -mx-6 px-6">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-4 py-2.5 text-[12px] font-semibold transition-all border-b-2 -mb-px tracking-wide
                ${activeTab === tab.key
                  ? 'text-coral-600 border-coral-500 bg-white/80'
                  : 'text-neutral-400 border-transparent hover:text-neutral-600 hover:border-neutral-300'
                }`}
            >
              {tab.label}
              {tab.key === 'plan' && issueExecutions.length > 0 && (
                <span className={`ml-1.5 text-[10px] font-mono ${activeTab === 'plan' ? 'text-coral-400' : 'text-neutral-300'}`}>{issueExecutions.length}</span>
              )}
              {tab.key === 'events' && sprintEvents.length > 0 && (
                <span className={`ml-1.5 text-[10px] font-mono ${activeTab === 'events' ? 'text-coral-400' : 'text-neutral-300'}`}>{sprintEvents.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'overview' && (
          <PageErrorBoundary>
            <MonitorOverviewTab
              sprint={sprint}
              epics={epics}
              issueExecutions={issueExecutions}
              sprites={sprites}
              burndownData={burndownData}
              telemetryEvents={telemetryEvents}
              sprintPhases={sprintPhases}
              errorSummary={errorSummary}
              sprintPlan={sprintPlan}
              qualityGates={qualityGates}
              tokenUsage={tokenUsage}
            />
          </PageErrorBoundary>
        )}

        {activeTab === 'plan' && (
          <PageErrorBoundary>
            <MonitorPlanTab
              sprint={sprint}
              epics={epics}
              issueExecutions={issueExecutions}
              telemetryEvents={telemetryEvents}
              timelineData={timelineData}
              childTree={childTree}
              batchWorkflowId={batchWorkflowId}
            />
          </PageErrorBoundary>
        )}

        {activeTab === 'events' && (
          <PageErrorBoundary>
            <MonitorEventsTab sprint={sprint} />
          </PageErrorBoundary>
        )}

        {activeTab === 'sprites' && (
          <PageErrorBoundary>
            <MonitorSpritesTab sprint={sprint} />
          </PageErrorBoundary>
        )}
      </div>
    </div>
  );
}
